'use client';

import { useState, useEffect, useMemo } from 'react';
import { authService } from '@/lib/authService';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('useLinkableWorkItemDocs');

interface LinkedDocument {
  document_id: string;
  document_type: string;
}

interface UseLinkableWorkItemDocsParams {
  linkableWorkItemId?: string | null;
}

/**
 * Fetches the financial documents linked to the linkable work item (appointment).
 *
 * Used to determine whether the appointment already has linked purchase orders,
 * which gates whether the "link to active appointment" toggle can be enabled
 * for procurement types in strict workflow mode.
 */
export function useLinkableWorkItemDocs({ linkableWorkItemId }: UseLinkableWorkItemDocsParams) {
  const [linkedDocs, setLinkedDocs] = useState<LinkedDocument[]>([]);

  useEffect(() => {
    if (!linkableWorkItemId) return;
    const token = authService.getTokens()?.access_token;
    if (!token) return;
    let cancelled = false;
    fetch(`/api/v1/work-items/${linkableWorkItemId}/financial-documents`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled) return;
        if (data?.success && Array.isArray(data.data?.linked_documents)) {
          setLinkedDocs(data.data.linked_documents.map((d: { document_id?: string; document_type?: string }) => ({
            document_id: String(d.document_id || ''),
            document_type: String(d.document_type || ''),
          })));
        } else {
          setLinkedDocs([]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (!isSilentFetchError(err)) logger.warn('[useLinkableWorkItemDocs] Failed to fetch linked documents:', err);
        setLinkedDocs([]);
      });
    return () => { cancelled = true; };
  }, [linkableWorkItemId]);

  const hasLinkedPOs = useMemo(
    () => linkedDocs.some(d => d.document_type === 'purchase_order'),
    [linkedDocs],
  );

  return {
    linkedDocs,
    hasLinkedPOs,
  };
}

export type UseLinkableWorkItemDocsReturn = ReturnType<typeof useLinkableWorkItemDocs>;
